import {
  INVOICE_ANALYSIS_STATE,
  getInvoiceAnalysisState,
  isReReviewStatus,
  isReviewBlockedClassification,
} from '@/modules/document/utils/invoice-analysis-state'
import { isRefreshAnalysisDocumentEligible } from '@/modules/document/utils/refresh-analysis-eligibility'

// FLAG:useDocuAi — WHICH surface does an expense/revenue form show: the central review component or
// the normal booking form?
//
// The rule lives HERE and only here. It previously lived entirely in expense-edit-page; when the
// revenue page needed the same surface, that would have created a second copy — exactly what
// invariant 1 („no duplicated flow logic") forbids it — and the two hosts would have diverged at the
// next rule change. A host supplies only its state questions, never the rule.
//
// Deliberately WITHOUT React and WITHOUT FileService: the hook (use-invoice-review-entry) performs
// the fetch before this. Only then is the rule testable in isolation — through redux/store,
// `FileService` pulls half the app into a Jest run (up to an ESM-only tag input), and for the same
// reason `isReReviewStatus` comes directly from the leaf module invoice-analysis-state rather than through the re-export in use-re-review-save.

// An invoice that still needs review.
const REVIEWABLE_STATES = [INVOICE_ANALYSIS_STATE.REVIEW, INVOICE_ANALYSIS_STATE.PARTIAL]

/**
 * ONE review component: an unconfirmed receipt opens the review for EVERY analysed invoice,
 * including an already reviewed one ('complete'). 'complete' previously fell back to the old form
 * to keep booking accessible — now that every review save immediately creates or updates the
 * expense/revenue, this is the normal case, and the reviewer ended up in the old form on the happy
 * path.
 */
export const isReviewEntryStatus = (status?: string | null): boolean =>
  REVIEWABLE_STATES.includes(status as never) || isReReviewStatus(status)

interface DocumentInvoiceDetailsLike {
  reviewStatus?: string | null
  [key: string]: unknown
}

/** Only the fields this rule reads — the rest of the file is irrelevant here. */
export interface ReviewFileLike {
  id?: string | number
  fileId?: string | number
  /** The S3 key — the identifier „Analyse-Problem melden" uses to name the receipt. */
  minioId?: string
  documentInvoiceDetails?: DocumentInvoiceDetailsLike | null
  /** The DMS/DocuAI lifecycle markers (analysisInProgress, analysisStartedAt, ocr, …). */
  metaData?: Record<string, unknown> | null
  [key: string]: unknown
}

export interface ReviewHostData {
  id?: string | number
  filesToAssign?: ReviewFileLike[]
  receipts?: ReviewFileLike[]
  [key: string]: unknown
}

/**
 * FLAG:useDocuAi — the linked receipt file whose DocuAI analysis is STILL RUNNING.
 *
 * Since plan omxweb-draft-receipt-instead-of-review-row (T2/T7, 2026-09-17) a receipt dropped into
 * Ausgaben / Einnahmen is an expense / revenue record from the upload terminal on — in state
 * 'toAnalyze' while DocuAI works, with a linked file that carries no documentInvoiceDetails yet.
 * Opened in that window (direct link, previous/next from a neighbour), the record has nothing to
 * review YET, but it is not a manual booking either: showing the empty form (0,00 €, today's date)
 * would present the invented placeholder values as if they were the receipt. The review surface
 * already knows this window — EmbeddedInvoiceReview renders the „Analyse läuft" placeholder next
 * to the document preview and takes the result over when it lands — so the entry opens it.
 *
 * The central lifecycle rule decides "running", including its 45-second client backstop
 * (ANALYZING_MAX_MS): a stale in-progress marker without a terminal does NOT open the review, the
 * host falls back to its form exactly as the list falls back to a static „In Analyse" cell.
 */
export const isAnalysingReviewFile = (file: ReviewFileLike | null | undefined): boolean => {
  if (!file) { return false }
  const state = getInvoiceAnalysisState(file)
  return state === INVOICE_ANALYSIS_STATE.ANALYZING || state === INVOICE_ANALYSIS_STATE.RETRYING
}

export const getRelatedFileIds = (data: ReviewHostData = {}): Array<string | number> =>
  [...(data.filesToAssign || []), ...(data.receipts || [])]
    .map(file => file?.fileId ?? file?.id)
    .filter((fileId): fileId is string | number => !!fileId)

export const shouldLoadReviewEntryFiles = ({
  enabled,
  canWrite,
  hasRelatedFiles,
}: {
  enabled: boolean
  canWrite: boolean
  hasRelatedFiles: boolean
}): boolean => enabled && canWrite && hasRelatedFiles

/**
 * The ONE chain both hosts use to pick their surface: an automatically opening pending review, or
 * the EXPLICIT request („Erneut prüfen" / the link from the details menu).
 *
 * TWO ids are deliberately NOT part of this chain, both for the same reason — the review may open
 * itself, but never over a form somebody is working in:
 *
 * `reviewedFileId`, the already reviewed invoice. Auto-opening the review for a checked or booked
 * receipt made „Bearbeiten" unusable: the reviewer was thrown back into the review of something
 * they had already booked, and the normal booking form — with its three accounting targets — was
 * unreachable.
 *
 * `uploadedFileId`, the file a DMS/DocuAI upload just created — LAST in the chain, and the position
 * is the point. Whoever uploads an invoice into Ausgaben/Einnahmen wants to check ITS data, so it
 * must open the review; but it must not overtake a receipt's OWN pending review, which is what it
 * did when it headed the chain — the reviewer was then checking the wrong document.
 *
 * It was briefly removed from here altogether, to fix „Ausgabe aus Lieferant erstellen ⇒ Beleg
 * hochladen ⇒ das Formular ist weg". That was the wrong half: the form and its typed values
 * disappeared because the host ALSO dispatched `clearReviewData()`, which empties
 * `state.receipt.edit.data`. Dropping this id instead took the main path with it — an upload then
 * never opened the analysis at all and left the user in a blank form (measured on dev11,
 * 2026-09-16). Only the wipe is gone now; cancelling the review returns to the form with the typed
 * values intact, because EmbeddedInvoiceReview clears the slice itself on mount and before seeding.
 *
 * Uploads from DOCUMENTS are untouched: they never pass through this rule — that surface is decided
 * by isEmbeddedInvoiceReview / the /documents/review route, and this function has exactly two
 * callers, expense-edit-page and revenue-edit-page.
 *
 * Both reviews stay reachable through `requestedReviewFileId`, i.e. because the user asked.
 *
 * Flag-OFF no host supplies any of these ids, so the result is null and the legacy form renders
 * exactly as before.
 */
export const resolveActiveReviewFileId = ({
  uploadedFileId,
  pendingReviewFileId,
  requestedReviewFileId,
}: {
  uploadedFileId?: string | null
  pendingReviewFileId?: string | null
  requestedReviewFileId?: string | null
}): string | null => pendingReviewFileId || requestedReviewFileId || uploadedFileId || null

export const shouldBlockForAutomaticReviewDecision = ({
  isAnalysingMode,
  activeReviewFileId,
  resolved,
}: {
  isAnalysingMode: boolean
  activeReviewFileId?: string | number | null
  resolved: boolean
}): boolean => isAnalysingMode && !activeReviewFileId && !resolved

export interface ReviewEntry {
  /**
   * The invoice for which the review component opens NOW (only in the unconfirmed state): an
   * analysed invoice awaiting its review, or — plan omxweb-draft-receipt-instead-of-review-row —
   * the upload record's linked file whose analysis is still running (the review surface shows the
   * analysis state with the document preview and takes the result over when it lands).
   */
  pendingReviewFileId: string | null
  /** The already reviewed invoice — supplies confirmed values and the completed status. */
  reviewedFileId: string | null
  reviewedDetails: DocumentInvoiceDetailsLike | null
  /**
   * The invoice for individual actions in the normal expense/revenue details.
   *
   * Prefer the already reviewed invoice, but fall back to a still-open review. An already booked
   * receipt must not throw itself into review, but
   * it still must offer the user an explicit entry
   * point. This exact case occurs for an Other Revenue with `reviewStatus='review'`.
   */
  reviewActionFileId: string | null
  reviewActionMinioId: string | null
  reviewActionFile: ReviewFileLike | null
  /** The analysed document for „Erneut analysieren", regardless of its review status. */
  refreshableFile: ReviewFileLike | null
  /**
   * FLAG:docuAiEnhancements — the S3 key of the same reviewed invoice, for
   * „Analyse-Problem melden" beside „Erneut prüfen".
   *
   * Here rather than in the host: the hook fetches the full file anyway (fetchFileInfo always
   * supplies `minioId`), and both hosts — expenses and revenues — need exactly the same value.
   * Loading it again in the host would be a second fetch for an existing field, and the rule
   * The rule „which file is being reviewed" would then exist twice.
   *
   * A file without `minioId` returns null, and the report action then renders nothing instead of
   * sending a report without a receipt reference.
   */
  reviewedMinioId: string | null
  /**
   * FLAG:docuAiEnhancements — the same reviewed invoice as a WHOLE file, for the receipt chat.
   *
   * `reviewedDetails` is insufficient: in addition to review data, `buildChatSituation` reads
   * `metaData.ocr`, `validationResult`, `eInvoice`, and the file name — without them, the chat does
   * not know whether it is handling an XML or paper invoice or how the analysis ended. The hook has
   * already fetched the file; passing it through here saves the host a second fetch for data already
   * in memory.
   */
  reviewedFile: ReviewFileLike | null
}

export const EMPTY_REVIEW_ENTRY: ReviewEntry = {
  pendingReviewFileId: null,
  reviewedFileId: null,
  reviewedDetails: null,
  reviewActionFileId: null,
  reviewActionMinioId: null,
  reviewActionFile: null,
  refreshableFile: null,
  reviewedMinioId: null,
  reviewedFile: null,
}

/**
 * The pure rule over the fetched files.
 *
 * ONE exclusion, learned from real harm: a classification with no review (contract) has nothing to
 * review here. A cancellation/credit note is NO LONGER excluded: the review explicitly allows
 * negative totals (edit.ts; positivity rule now applies only to the expense form), the upload path
 * (handleDmsReceiptUploaded) and Documents review have long reviewed them, and the server derives a
 * correct NEGATIVE draft expense or skips it with a named reason (receiptReviewCaseService:
 * negativeBookingUnsupported / cancellationSignUnknown) — so save no longer lies. The old filter
 * made the same credit note reviewable on upload but unreachable once it was already attached to
 * the receipt.
 *
 * `isAnalysingMode` decides AUTO-opening: a booked receipt („Offen") gets only the explicit action
 * „Erneut prüfen". Automatically opening the review there threw the reviewer back into reviewing
 * something they had already booked — with no way out.
 *
 * A linked file whose analysis is STILL RUNNING (isAnalysingReviewFile) opens the same surface in
 * the unconfirmed state: the upload record of plan omxweb-draft-receipt-instead-of-review-row
 * exists from the upload terminal on, and its form would only show the invented placeholder values
 * until the result lands. An analysed invoice awaiting review always wins over a running one, so a
 * re-analysis over an existing review keeps reviewing the existing data.
 */
export const resolveReviewEntry = (
  files: Array<ReviewFileLike | null>,
  { isAnalysingMode }: { isAnalysingMode: boolean },
): ReviewEntry => {
  const reviewable = files.filter(file => file
    && !isReviewBlockedClassification(file))
  const pending = reviewable.find(file => isReviewEntryStatus(file?.documentInvoiceDetails?.reviewStatus))
  // Only the AUTOMATIC entry knows the still-running file: it has no review data yet, so the explicit
  // actions („Erneut prüfen", „Erneut analysieren", the report) keep addressing an analysed invoice.
  const analysing = pending ? null : reviewable.find(file => isAnalysingReviewFile(file))
  const autoOpen = pending ?? analysing
  const reviewed = reviewable.find(file => isReReviewStatus(file?.documentInvoiceDetails?.reviewStatus))
  const reviewAction = reviewed ?? pending
  // Every unlabelled action in the receipt topbar must address the SAME attachment. If a review
  // candidate exists, never silently fall through to a different file just because that neighbour
  // happens to be refreshable. Without any review candidate, the standalone action may use the
  // first fully eligible legacy/cancellation invoice attachment.
  const refreshableFile = reviewAction
    ? (isRefreshAnalysisDocumentEligible(reviewAction) ? reviewAction : null)
    : (files.find(file => isRefreshAnalysisDocumentEligible(file)) ?? null)
  return {
    pendingReviewFileId: autoOpen && isAnalysingMode ? `${autoOpen.id}` : null,
    reviewedFileId: reviewed ? `${reviewed.id}` : null,
    reviewedDetails: reviewed?.documentInvoiceDetails ?? null,
    reviewActionFileId: reviewAction ? `${reviewAction.id}` : null,
    reviewActionMinioId: typeof reviewAction?.minioId === 'string' ? reviewAction.minioId : null,
    reviewActionFile: reviewAction ?? null,
    refreshableFile,
    reviewedMinioId: typeof reviewed?.minioId === 'string' ? reviewed.minioId : null,
    reviewedFile: reviewed ?? null,
  }
}
